import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { parseCalendarDate } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { anyCalendarConnected, calendarConnections, calendarDays, calendarKeys, sourcesNeedingReconnect } from '../../services/calendar';
import { useProfile } from '../../services/profile';
import { tapLink } from '../../components/styles';
import CalendarEventItem from './CalendarEventItem';
import { useTodaySections } from '../home/todaySections';

/**
 * Today's events from every connected calendar, busy ones marked. Nothing is
 * shown for a user who has connected no calendar, and a connected user's
 * empty day is the heading alone, as every Today section is. Like them it
 * starts collapsed: the heading with how many events and how many are busy.
 * A calendar that needs reconnecting, or could not be loaded, says so either
 * way.
 */
export default function TodayCalendar({ today }: { today: string }) {
  const connection = useQuery({ queryKey: calendarKeys.connections, queryFn: calendarConnections });
  const tomorrow = format(addDays(parseCalendarDate(today), 1), 'yyyy-MM-dd');
  const connected = connection.isSuccess && anyCalendarConnected(connection.data);
  const reconnect = sourcesNeedingReconnect(connection.data);
  const events = useQuery({
    queryKey: calendarKeys.days(today, tomorrow),
    queryFn: () => calendarDays(today, tomorrow),
    enabled: connected,
  });
  const timeZone = useProfile().data?.timezone ?? undefined;

  const sections = useTodaySections();
  if (!connected) return null;
  const busy = events.data?.filter((e) => e.blocks).length ?? 0;
  const count = events.data?.length ?? 0;
  const open = count > 0 && sections.isOpen('calendar');

  return (
    <section className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${open || events.isError || reconnect.length ? 'mb-3' : ''}`}>
        {count > 0 ? (
          <button type="button" aria-expanded={open} onClick={() => sections.toggle('calendar')} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-left">
            {open ? <ChevronDown className="w-4 h-4 text-on-surface-variant" /> : <ChevronRight className="w-4 h-4 text-on-surface-variant" />}
            <CalendarDays className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Calendar</h2>
            <span className="text-sm text-on-surface-variant">{count}</span>
          </button>
        ) : (
          <>
            <CalendarDays className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Calendar</h2>
          </>
        )}
        {busy > 0 && <span className="text-sm text-on-surface-variant">{busy} busy</span>}
        <Link to="/calendar" className={`${tapLink} ml-auto text-sm text-primary`}>
          Week
        </Link>
      </div>
      {reconnect.map((source) => (
        <p key={source} role="status" className="text-sm text-error mb-2">
          {source} needs you to connect again.{' '}
          <Link to="/integrations" className="underline">
            Integrations
          </Link>
        </p>
      ))}
      {events.isError && <p className="text-sm text-error">Could not load your calendar: {errorMessage(events.error)}</p>}
      {open && events.data && (
        <ul className="space-y-2">
          {events.data.map((event) => (
            <li key={event.id}>
              <CalendarEventItem event={event} day={today} timeZone={timeZone} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
